import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { logSearch } from '@/lib/logger'
import { groupCustomerMatches } from '@/utils/customerGrouping'
import { getOrderRoutePath } from '@/features/delivery'
import { useUserOrganization } from '@/hooks/useUserOrganization'
import type { CustomerMatch } from '@/utils/customerGrouping'

type SearchMethod = 'manual' | 'scanner' | 'pickup_code'

interface CustomerOrder {
  order_id: number
  customer_name: string
  status: string
}

interface UseOrderSearchResult {
  isSearching: boolean
  error: string | null
  customerMatches: CustomerMatch[]
  customerOrders: CustomerOrder[]
  selectedCustomer: string | null
  searchAndNavigate: (orderId: string, method?: SearchMethod) => Promise<void>
  searchByPickupCode: (code: string) => Promise<void>
  searchCustomers: (query: string) => Promise<void>
  selectCustomer: (customerName: string) => Promise<void>
  clearError: () => void
  clearResults: () => void
}

export function useOrderSearch(): UseOrderSearchResult {
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customerMatches, setCustomerMatches] = useState<CustomerMatch[]>([])
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null)
  const navigate = useNavigate()
  const { data: membership } = useUserOrganization()

  const clearError = useCallback(() => setError(null), [])
  const clearResults = useCallback(() => {
    setCustomerMatches([])
    setCustomerOrders([])
    setSelectedCustomer(null)
  }, [])

  const searchAndNavigate = useCallback(async (searchQuery: string, method: SearchMethod = 'manual') => {
    const orderId = parseInt(searchQuery)
    if (isNaN(orderId)) {
      setError('Ugyldig ordrenummer')
      return
    }

    // Log search attempt (fire-and-forget)
    logSearch({ orderId: searchQuery, method })

    setIsSearching(true)
    setError(null)

    try {
      // HOT search - check Supabase first
      const { data: order, error: hotError } = await supabase
        .schema('delivery')
        .from('app_orders')
        .select('order_id, status')
        .eq('order_id', orderId)
        .maybeSingle()

      if (order && !hotError) {
        // Found in HOT - navigate based on status
        const status = order.status || 'import'
        navigate(getOrderRoutePath(orderId, status))
        return
      }

      // COLD search - fetch from erp.orders history and copy to app_orders
      const { data: erpOrder, error: erpError } = await supabase
        .schema('erp')
        .from('orders')
        .select('*')
        .eq('order_id', orderId)
        .maybeSingle()

      if (erpError) {
        setError('Feil ved sok i historikk')
        return
      }

      if (!erpOrder) {
        setError('Ordre ikke funnet')
        return
      }

      // Copy to delivery.app_orders with status 'import'
      const { error: upsertError } = await supabase
        .schema('delivery')
        .from('app_orders')
        .upsert({
          organization_id: erpOrder.organization_id,
          order_id: erpOrder.order_id,
          customer_id: erpOrder.customer_id,
          customer_name: erpOrder.customer_name,
          reference: erpOrder.reference,
          external_ref: erpOrder.your_ref,
          order_date: erpOrder.order_date,
          status: 'import',
          erp_raw_data: erpOrder.raw_json,
          source: 'erp_import',
          created_by: 'user',
        }, { onConflict: 'order_id' })

      if (upsertError) {
        setError('Kunne ikke importere ordre')
        return
      }

      // COLD success - navigate to import view
      navigate(`/delivery/${orderId}/import`)
    } catch (err) {
      console.error('Order search error:', err)
      setError('Nettverksfeil: ' + (err instanceof Error ? err.message : 'Ukjent feil'))
    } finally {
      setIsSearching(false)
    }
  }, [navigate])

  // Search by pickup code and navigate to pending view
  const searchByPickupCode = useCallback(async (code: string) => {
    if (!membership?.organization_id) {
      setError('Ikke innlogget')
      return
    }

    // Log search attempt (fire-and-forget)
    logSearch({ orderId: `pickup:${code}`, method: 'pickup_code' })

    setIsSearching(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase
        .schema('delivery')
        .rpc('get_order_by_pickup_code', {
          p_organization_id: membership.organization_id,
          p_pickup_code: code,
        })

      if (rpcError) {
        setError('Feil ved søk etter hentekode')
        return
      }

      const order = data?.[0]
      if (!order) {
        setError('Hentekode ikke funnet')
        return
      }

      // Navigate to pending view (pickup codes are only for pending orders)
      // Pass fromPickupCode flag to skip phone validation
      navigate(`/delivery/${order.order_id}/pending`, {
        state: { fromPickupCode: true }
      })
    } catch (err) {
      console.error('Pickup code search error:', err)
      setError('Nettverksfeil: ' + (err instanceof Error ? err.message : 'Ukjent feil'))
    } finally {
      setIsSearching(false)
    }
  }, [navigate, membership?.organization_id])

  // Search for customers (autocomplete) - returns top 3 with order count
  const searchCustomers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setCustomerMatches([])
      return
    }

    setIsSearching(true)
    setError(null)

    try {
      // Get orders matching customer name, excluding 'import' status
      const { data: orders, error: searchError } = await supabase
        .schema('delivery')
        .from('app_orders')
        .select('customer_id, customer_name')
        .neq('status', 'import')
        .ilike('customer_name', `%${query}%`)

      if (searchError) {
        setError('Søkefeil')
        return
      }

      if (!orders || orders.length === 0) {
        setCustomerMatches([])
        return
      }

      // Group by customer_id and get top 3 matches
      const matches = groupCustomerMatches(orders)
      setCustomerMatches(matches)
    } catch {
      setError('Nettverksfeil')
    } finally {
      setIsSearching(false)
    }
  }, [])

  // Select a customer and fetch their orders
  const selectCustomer = useCallback(async (customerName: string) => {
    setIsSearching(true)
    setSelectedCustomer(customerName)
    setCustomerMatches([])

    try {
      const { data: orders, error: fetchError } = await supabase
        .schema('delivery')
        .from('app_orders')
        .select('order_id, customer_name, status')
        .neq('status', 'import')
        .eq('customer_name', customerName)
        .order('order_id', { ascending: false })
        .limit(20)

      if (fetchError) {
        setError('Kunne ikke hente ordrer')
        return
      }

      if (!orders || orders.length === 0) {
        setError('Ingen ordrer funnet')
        return
      }

      // If only one order, navigate directly
      if (orders.length === 1) {
        const order = orders[0]
        navigate(getOrderRoutePath(order.order_id, order.status))
        return
      }

      setCustomerOrders(orders)
    } catch {
      setError('Nettverksfeil')
    } finally {
      setIsSearching(false)
    }
  }, [navigate])

  return {
    isSearching,
    error,
    customerMatches,
    customerOrders,
    selectedCustomer,
    searchAndNavigate,
    searchByPickupCode,
    searchCustomers,
    selectCustomer,
    clearError,
    clearResults,
  }
}
